/**
 * Chrome for the standalone auth pages (reset password, update password): a
 * page consumes one map so its roles cannot mix surfaces.
 */

import {
  AUTH_BUTTON_CLASS,
  AUTH_DESCRIPTION_CLASS,
  AUTH_HEADING_CLASS,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_LINK_CLASS,
  AUTH_PASSWORD_INPUT_CLASS,
} from '@/components/auth/auth-styles';

export interface AuthPageChrome {
  readonly heading: string;
  readonly description: string;
  readonly label: string;
  readonly input: string;
  readonly passwordInput: string;
  readonly primaryButton: string;
  readonly link: string;
  readonly mutedText: string;
  /** An emphasised value inside body copy (the address a link was sent to). */
  readonly emphasis: string;
  /** The password-visibility eye, tinted for the surface it sits on. */
  readonly revealIcon: string;
  /** One cell of the 6-digit step-up input; empty keeps the OTP primitive's defaults. */
  readonly codeCell: string;
  /** The quiet full-width action sitting under `primaryButton`. */
  readonly secondaryButton: string;
  /**
   * An emphasised value inside `mutedText`, not inside `description`. It is its
   * own role because the surrounding copy is already dimmed: reusing
   * `emphasis` here would read as the brighter body-copy weight and flatten the
   * distinction the muted line depends on.
   */
  readonly mutedEmphasis: string;
}

const WEB_CHROME: AuthPageChrome = {
  heading: AUTH_HEADING_CLASS,
  description: AUTH_DESCRIPTION_CLASS,
  label: AUTH_LABEL_CLASS,
  input: AUTH_INPUT_CLASS,
  passwordInput: AUTH_PASSWORD_INPUT_CLASS,
  primaryButton: AUTH_BUTTON_CLASS,
  link: AUTH_LINK_CLASS,
  mutedText: 'text-xs text-landing-fg/30',
  emphasis: 'font-medium text-landing-fg',
  revealIcon: 'text-landing-fg/25',
  codeCell: '',
  secondaryButton: 'text-landing-fg/45 hover:bg-transparent hover:text-landing-fg/70',
  mutedEmphasis: 'font-medium text-landing-fg/60',
};

/**
 * The web text back link, shared by every standalone auth page that carries one.
 *
 * Desktop wears `entryBackLinkClass`, web wears this. Each is a WHOLE class
 * string rather than a base plus overrides — they disagree on size and colour,
 * and resolving that by class order would rest on tailwind-merge grouping
 * project-specific colour names it has no config for.
 */
export const WEB_BACK_LINK_CLASS =
  'mb-4 h-auto p-0 text-xs font-medium text-landing-fg/45 hover:bg-transparent hover:text-landing-fg/70';

export function getAuthPageChrome(): AuthPageChrome {
  return WEB_CHROME;
}
